import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { OkAny } from "@/types/OkAny.type";

/** Merge class names.
 * @param inputs The class names to merge.
 * @returns The merged class names.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const safeParseInt = <Fallback = number>(
  value: OkAny,
  options: { fallback?: Fallback; onError?: "throw" | "return"; stripNonNumeric?: boolean } = {
    fallback: 0 as Fallback,
    onError: "return",
    stripNonNumeric: false,
  },
): Fallback => {
  const { fallback, onError, stripNonNumeric } = options;

  const reject = () => {
    if (onError === "throw") throw new Error(`Failed to parse integer from value: ${value}`);
    return fallback as Fallback;
  };

  if (value === null || value === undefined || value === "") return reject(); // Avoid rejecting `0`
  if (typeof value !== "string" && typeof value !== "number") return reject(); // Sanity check

  const sanitizedValue = stripNonNumeric ? String(value).replace(/\D+/g, "") : String(value);
  const num = parseInt(sanitizedValue, 10);

  return Number.isNaN(num) ? reject() : (num as Fallback);
};

/** Safely parse a boolean from a string or boolean value.
 * @param value The value to parse. Supported truthy values: `"true"`, `"1"`, `"yes"`, `"y"`, `"t"`. Supported falsy values: `"false"`, `"0"`, `"no"`, `"n"`, `"f"`.
 * @param options Options for parsing.
 * @param options.fallback The fallback value to return if parsing fails. Defaults to `false`.
 * @param options.onError What to do if parsing fails. Defaults to `"return"`.
 * @returns The parsed boolean value.
 */
export const safeParseBool = (
  value: string | null,
  options: { fallback?: boolean; onError?: "throw" | "return" } = {
    fallback: false,
    onError: "return",
  },
): boolean => {
  const { fallback, onError } = options;

  const reject = () => {
    if (onError === "throw") throw new Error(`Failed to parse boolean from value: ${value}`);
    return typeof fallback === "boolean" ? fallback : false;
  };

  if (value === null || value === undefined || value === "") return reject(); // Avoid rejecting `false`
  if (typeof value !== "string" && typeof value !== "boolean") return reject(); // Sanity check

  if (typeof value === "boolean") return value;

  const sanitizedValue = String(value).toLowerCase();
  if (["true", "1", "yes", "y", "t", "on"].includes(sanitizedValue)) return true;
  if (["false", "0", "no", "n", "f", "off"].includes(sanitizedValue)) return false;

  return reject();
};
